import { isPast } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  MonetaryAmount,
  PromotionalEventCompletionState,
} from '__generated__/globalTypes';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyS, LabelS } from 'atoms/typography';
import { ConfigQuery } from 'contexts/config/types';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { isEqual, zeroMonetaryAmount } from 'hooks/useMonetaryAmount';
import { isType } from 'lib/gql';

import { ActivePromotionalEventTimeLeft } from '../ActivePromotionalEventTimeLeft';
import { Cta } from '../Cta';
import { getCCRewardRange } from '../Details';
import cover from '../Details/assets/cover.jpg';
import { Remaining } from '../Remaining';
import { SpentProgress } from '../SpentProgress';

const Root = styled(Vertical).attrs({ center: true })`
  text-align: center;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  color: var(--c-white);
  border: 1px solid;
  background:
    linear-gradient(0deg, var(--c-nd-50), var(--c-nd-50)),
    linear-gradient(0deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
    url(${cover}) center center no-repeat;
  background-size: cover;
`;

const StyledLink = styled(Link)`
  color: var(--c-nd-600);
  text-decoration: underline;
`;

const Rules = ({ min, max }: { min: MonetaryAmount; max: MonetaryAmount }) => {
  const { currentUser, fiatCurrency } = useCurrentUserContext();
  const { formatNumber } = useIntl();

  const { main } = useAmountWithConversion({ monetaryAmount: min });
  const { main: mainMax } = useAmountWithConversion({ monetaryAmount: max });
  if (!currentUser?.completablePromotionalEvent) return null;
  return (
    <FormattedMessage
      id="AddFundsToFiatWallet.Promo.remaining"
      defaultMessage="Deposit {amount}+ for a guaranteed {min}-{max} in market credits!"
      values={{
        amount: formatNumber(
          (currentUser.completablePromotionalEvent.eligibilityRules
            .minimumDepositAmount || 0) / 100,
          {
            style: 'currency',
            currency: fiatCurrency.code,
            minimumFractionDigits: 0,
          }
        ),
        min: main,
        max: mainMax,
      }}
    />
  );
};

const Title = ({
  completablePromotionalEvent,
}: {
  completablePromotionalEvent: NonNullable<
    NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent']
  >;
}) => {
  const { main } = useAmountWithConversion({
    monetaryAmount:
      completablePromotionalEvent.promotionalEventCompletion?.targetAmount ||
      zeroMonetaryAmount,
  });
  const ccRange = getCCRewardRange(
    completablePromotionalEvent.rewardsProbabilisticBundleConfig
  );
  const { main: rewardAmount } = useAmountWithConversion({
    monetaryAmount: ccRange?.min || zeroMonetaryAmount,
  });

  if (
    completablePromotionalEvent.completionRules.completion_percentage !== 100 &&
    isType(completablePromotionalEvent, 'DepositPromotionalEvent')
  )
    return (
      <FormattedMessage
        id="AddFundsToFiatWallet.Promo.compactBanner.inProgress.description"
        defaultMessage="Spend at least {percentage, number, ::percent} in Instant Buy or Auctions to get your reward."
        values={{
          percentage:
            completablePromotionalEvent.completionRules.completion_percentage /
            100,
        }}
      />
    );
  if (completablePromotionalEvent.promotionalEventCompletion) {
    if (ccRange && isEqual(ccRange.min, ccRange.max)) {
      return (
        <FormattedMessage
          id="AddFundsToFiatWallet.Promo.compactBanner.inProgress.withFixedConversionCredit.description"
          defaultMessage="Spend at least {amount} in Instant Buy or Auctions to get {ccAmount} in market credits."
          values={{
            amount: main,
            ccAmount: rewardAmount,
          }}
        />
      );
    }
    return (
      <FormattedMessage
        id="AddFundsToFiatWallet.Promo.compactBanner.inProgress.amount.description"
        defaultMessage="Spend at least {amount} in Instant Buy or Auctions to get your reward."
        values={{
          amount: main,
        }}
      />
    );
  }
  return null;
};

export const CompactBanner = ({
  showDepositCta,
  showInprogress,
}: {
  showInprogress?: boolean;
  showDepositCta?: boolean;
}) => {
  const { currentUser } = useCurrentUserContext();

  if (!currentUser) return null;

  const { completablePromotionalEvent } = currentUser;

  if (!completablePromotionalEvent) return null;

  const { promotionalEventCompletion } = completablePromotionalEvent;

  if (promotionalEventCompletion) {
    if (
      showInprogress &&
      promotionalEventCompletion.aasmState ===
        PromotionalEventCompletionState.PENDING &&
      !isPast(promotionalEventCompletion.expiresAt)
    ) {
      return (
        <Root>
          <Horizontal>
            <ActivePromotionalEventTimeLeft />
          </Horizontal>
          <BodyS>
            <Title completablePromotionalEvent={completablePromotionalEvent} />
          </BodyS>
          <SpentProgress />
        </Root>
      );
    }
    return null;
  }

  if (
    !completablePromotionalEvent ||
    isPast(completablePromotionalEvent.endDate)
  )
    return null;

  const ccRange = getCCRewardRange(
    completablePromotionalEvent.rewardsProbabilisticBundleConfig
  );

  if (!ccRange) return null;

  const { max, min } = ccRange;

  return (
    <Root>
      {isType(completablePromotionalEvent, 'DepositPromotionalEvent') && (
        <Remaining
          completedParticipants={
            completablePromotionalEvent.completedParticipants
          }
        />
      )}

      <LabelS bold>
        <Rules min={min} max={max} />{' '}
        <StyledLink to={completablePromotionalEvent.blogUrl || ''}>
          <FormattedMessage
            id="AddFundsToFiatWallet.Promo.moreDetails"
            defaultMessage="More details"
          />
        </StyledLink>
      </LabelS>
      {showDepositCta && <Cta color="quaternary" />}
    </Root>
  );
};
